import React, { useState } from 'react'
import './InterviewPreview.scss'

function InterviewPreview() {
  const [activeTab, setActiveTab] = useState<'overview' | 'process' | 'company' | 'people' | 'questions'>('overview')
  // Anonymized sample data
  const sampleData = {
    jobTitle: 'Senior Software Engineer - Platform Development',
    fitScore: 82,
    summary: 'You have a strong technical background that aligns well with the team\'s needs. Your extensive experience with modern technologies, scalable systems, and developer infrastructure makes you a compelling candidate for this senior engineering role.',
    strengths: [
      { title: 'Technical expertise:', description: 'Deep experience with modern frameworks, TypeScript, and cloud technologies' },
      { title: 'Developer infrastructure:', description: 'Proven track record building developer tools, monitoring systems, and deployment automation' },
      { title: 'Scalability focus:', description: 'Experience designing and implementing scalable, fault-tolerant systems' },
      { title: 'Cross-platform development:', description: 'Experience with modern frameworks for web and mobile applications' },
      { title: 'Mentorship and leadership:', description: 'Strong history of mentoring junior developers and leading teams' },
      { title: 'Cloud and monitoring:', description: 'Extensive experience with cloud services and performance monitoring tools' }
    ],
    gaps: [
      { title: 'Specific tech stack:', description: 'While you have relevant experience, you may need to familiarize yourself with the company\'s specific internal tools and architecture' },
      { title: 'Domain specialization:', description: 'The role specifically mentions working with platform-specific features, which may require learning about their specific processes' },
      { title: 'Team dynamics:', description: 'While you have infrastructure experience, the role focuses specifically on team collaboration which may have unique requirements' }
    ],
    insights: 'Your experience building developer infrastructure and monitoring frameworks directly translates to the team\'s mission. Your work on scalable systems, particularly real-time systems and monitoring dashboards, demonstrates your ability to build tools that improve developer and customer experiences. The combination of your technical skills with your infrastructure background makes you uniquely positioned to contribute to the company\'s platform initiatives.',
    preparationActions: [
      { title: 'Deep dive into engineering culture:', description: 'Review the company\'s tech blog to understand their engineering philosophy and recent technical challenges' },
      { title: 'Brush up on advanced patterns:', description: 'Practice with modern development patterns to demonstrate cutting-edge architecture knowledge' },
      { title: 'Study developer productivity tools:', description: 'Explore modern CI/CD practices to discuss automation strategies' },
      { title: 'Prepare platform stories:', description: 'Reflect on your experience with scalable systems - these are directly relevant to platform development' },
      { title: 'Practice system design scenarios:', description: 'Use system design resources to prepare for architecture discussions around platform tools' },
      { title: 'Review best practices:', description: 'Solidify your knowledge with technical handbooks to discuss type-safe development' }
    ]
  }

  const circumference = 2 * Math.PI * 54
  const strokeDashoffset = circumference * (1 - sampleData.fitScore / 100)

  return (
    <section className="interview-preview-section py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="text-center mb-5">
              <h2 
                className="fw-bold mb-3 interview-preview-heading"
                style={{
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                  fontSize: '36px',
                  lineHeight: '44px',
                  letterSpacing: '-0.02em'
                }}
              >
                Ace Every Interview with <span className="interview-preview-accent">AI-Powered Prep</span>
              </h2>
              <p 
                className="text-muted interview-preview-subtitle"
                style={{
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif",
                  fontSize: '18px',
                  lineHeight: '28px',
                  maxWidth: '700px',
                  margin: '0 auto'
                }}
              >
                Get personalized insights, fit analysis, and preparation strategies for every job you apply to.
              </p>
            </div>

            {/* Preview Modal */}
            <div className="interview-preview-modal">
              <div className="interview-preview-header">
                <h3 className="interview-preview-title">
                  <span>📚 Interview Preparation</span>
                  <span className="d-none d-sm-inline"> - </span>
                  <span className="interview-preview-job-title">{sampleData.jobTitle}</span>
                </h3>
              </div>

              <div className="interview-preview-body">
                <div className="interview-preview-tabs">
                  <button 
                    className={`interview-preview-tab ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                  >
                    <span className="tab-icon">📊</span>
                    <span>Overview</span>
                  </button>
                  <button 
                    className={`interview-preview-tab ${activeTab === 'process' ? 'active' : ''}`}
                    onClick={() => setActiveTab('process')}
                  >
                    <span className="tab-icon">🔄</span>
                    <span>Process</span>
                  </button>
                  <button 
                    className={`interview-preview-tab ${activeTab === 'company' ? 'active' : ''}`}
                    onClick={() => setActiveTab('company')}
                  >
                    <span className="tab-icon">🏢</span>
                    <span>Company</span>
                  </button>
                  <button 
                    className={`interview-preview-tab ${activeTab === 'people' ? 'active' : ''}`}
                    onClick={() => setActiveTab('people')}
                  >
                    <span className="tab-icon">👥</span>
                    <span>People</span>
                  </button>
                  <button 
                    className={`interview-preview-tab ${activeTab === 'questions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('questions')}
                  >
                    <span className="tab-icon">❓</span>
                    <span>Question Bank</span>
                  </button>
                </div>

                <div className="interview-preview-content">
                  {activeTab === 'overview' && (
                    <div className="overview-content">
                      <div className="fit-analysis-section">
                        {/* Fit Score Card */}
                      <div className="fit-score-card">
                        <h3 className="fit-score-title">Job Fit Score</h3>
                        <div className="fit-score-visualization">
                          <div className="fit-score-circle">
                            <svg className="fit-score-svg" viewBox="0 0 120 120">
                              <circle
                                className="fit-score-bg"
                                cx="60"
                                cy="60"
                                r="54"
                                fill="none"
                                stroke="#e0e0e0"
                                strokeWidth="12"
                              />
                              <circle
                                className="fit-score-progress"
                                cx="60"
                                cy="60"
                                r="54"
                                fill="none"
                                stroke="#4caf50"
                                strokeWidth="12"
                                strokeLinecap="round"
                                strokeDasharray={circumference}
                                strokeDashoffset={strokeDashoffset}
                                transform="rotate(-90 60 60)"
                              />
                            </svg>
                            <div className="fit-score-value">
                              <span className="fit-score-number">{sampleData.fitScore}</span>
                              <span className="fit-score-label">/ 100</span>
                            </div>
                          </div>
                          <div className="fit-score-description">
                            <p className="fit-score-text excellent">Excellent Match</p>
                          </div>
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="fit-analysis-card">
                        <h4 className="fit-analysis-heading">Summary</h4>
                        <div className="fit-analysis-content">
                          <p>{sampleData.summary}</p>
                        </div>
                      </div>

                      {/* Strengths */}
                      <div className="fit-analysis-card fit-analysis-strengths">
                        <h4 className="fit-analysis-heading">Strengths</h4>
                        <div className="fit-analysis-content">
                          <ul>
                            {sampleData.strengths.map((strength, index) => (
                              <li key={index}>
                                <strong>{strength.title}</strong> {strength.description}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Gaps */}
                      <div className="fit-analysis-card fit-analysis-gaps">
                        <h4 className="fit-analysis-heading">Gaps</h4>
                        <div className="fit-analysis-content">
                          <ul>
                            {sampleData.gaps.map((gap, index) => (
                              <li key={index}>
                                <strong>{gap.title}</strong> {gap.description}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Insights */}
                      <div className="fit-analysis-card fit-analysis-insights">
                        <h4 className="fit-analysis-heading">Insights & Recommendations</h4>
                        <div className="fit-analysis-content">
                          <p>{sampleData.insights}</p>
                        </div>
                      </div>

                      {/* Preparation Actions */}
                      <div className="fit-analysis-card fit-analysis-preparation">
                        <h4 className="fit-analysis-heading">Interview Preparation Actions</h4>
                        <div className="fit-analysis-content">
                          <p>You're in a great position to excel in this role! Here are specific actions you can take to strengthen your candidacy:</p>
                          <ul>
                            {sampleData.preparationActions.map((action, index) => (
                              <li key={index}>
                                <strong>{action.title}</strong> {action.description}
                              </li>
                            ))}
                          </ul>
                          <p style={{ marginTop: '1rem' }}>
                            Remember, your background at top tech companies and your specific experience with developer infrastructure gives you a strong foundation. Focus on connecting your past successes to the company's specific challenges around scaling their platform!
                          </p>
                        </div>
                      </div>

                      {/* Disclaimer */}
                      <div className="fit-analysis-disclaimer">
                        <p className="disclaimer-text">
                          <small>
                            <strong>Disclaimer:</strong> The information provided is sourced from anonymous data and we cannot guarantee its accuracy. This analysis is for informational purposes only and should not be the sole basis for career decisions.
                          </small>
                        </p>
                      </div>
                    </div>
                  </div>
                  )}

                  {activeTab === 'process' && (
                    <div className="process-content">
                      <div className="company-enrichment-section">
                        <div className="company-enrichment-card">
                          <div className="company-overview">
                            <h1>Interview Process for {sampleData.jobTitle}</h1>
                            
                            <p>The interview process for a {sampleData.jobTitle} position typically involves several key steps. Each step has specific goals and expectations, and preparing adequately can significantly enhance your chances of success.</p>
                            
                            <h2>1. Application Submission</h2>
                            <p>This is the first step where you submit your resume and cover letter through the company's careers page.</p>
                            <ul>
                              <li><strong>What They Look For:</strong> Relevant experience, technical skills, and a passion for technology and innovation.</li>
                              <li><strong>Preparation Tips:</strong> Tailor your resume to highlight your relevant development experience, projects, and any relevant technologies. Ensure your cover letter reflects your enthusiasm for the company and the role.</li>
                            </ul>
                            
                            <h2>2. Initial Screening</h2>
                            <p>After your application is reviewed, you may have an initial phone screening with a recruiter.</p>
                            <ul>
                              <li><strong>What They Look For:</strong> Cultural fit, communication skills, and foundational technical knowledge.</li>
                              <li><strong>Preparation Tips:</strong> Be ready to discuss your previous job roles, projects, and why you want to work at this company. Familiarize yourself with the company's culture and values.</li>
                            </ul>
                            
                            <h2>3. Technical Assessment</h2>
                            <p>This step usually involves a coding test or a technical interview where you'll solve programming problems.</p>
                            <ul>
                              <li><strong>What They Look For:</strong> Problem-solving abilities, coding skills, and understanding of relevant technologies.</li>
                              <li><strong>Preparation Tips:</strong> Practice coding problems on platforms like LeetCode or HackerRank. Review relevant frameworks and concepts. Be prepared to explain your thought process and approach to problem-solving.</li>
                            </ul>
                            
                            <h2>4. On-Site Interviews (or Virtual Interviews)</h2>
                            <p>This stage typically consists of multiple rounds of interviews with various team members.</p>
                            <ul>
                              <li><strong>What They Look For:</strong> Technical proficiency, collaboration skills, and alignment with company values.</li>
                              <li><strong>Preparation Tips:</strong> Prepare to discuss your previous projects in detail, including challenges faced and how you overcame them. Be ready to demonstrate your technical skills through whiteboard coding or live coding sessions. Understand the principles of user experience and design as they relate to your role.</li>
                            </ul>
                            
                            <h2>5. Behavioral Interview</h2>
                            <p>This interview focuses on assessing your soft skills and cultural fit within the organization.</p>
                            <ul>
                              <li><strong>What They Look For:</strong> Values alignment, teamwork, and leadership qualities.</li>
                              <li><strong>Preparation Tips:</strong> Use the STAR method (Situation, Task, Action, Result) to structure your answers. Reflect on past experiences that demonstrate your ability to work in a team, handle conflict, and contribute to a positive workplace culture.</li>
                            </ul>
                            
                            <h2>6. Final Decision and Offer</h2>
                            <p>If you successfully pass through all the interview stages, you will receive a job offer.</p>
                            <ul>
                              <li><strong>What They Look For:</strong> Overall fit for the role and the organization.</li>
                              <li><strong>Preparation Tips:</strong> Be prepared to negotiate your offer. Research salary ranges and benefits typical for the position and come equipped with data to support your requests.</li>
                            </ul>
                            
                            <h2>7. Onboarding</h2>
                            <p>Once you accept the offer, you will go through the onboarding process.</p>
                            <ul>
                              <li><strong>What They Look For:</strong> Your willingness to integrate into the team and adapt to the company culture.</li>
                              <li><strong>Preparation Tips:</strong> Engage with any pre-onboarding materials provided and familiarize yourself with the tools and technologies used at the company.</li>
                            </ul>
                            
                            <p>By understanding each step of the interview process and preparing accordingly, you can significantly increase your chances of landing the {sampleData.jobTitle} position.</p>
                          </div>
                          <div className="company-ai-disclaimer">
                            <small>⚠️ This information is gathered from anonymous sources and we do not guarantee its correctness. Please verify important details independently.</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'company' && (
                    <div className="company-content">
                      <div className="company-enrichment-section">
                        <div className="company-enrichment-card">
                          <div className="company-overview">
                            <h1>Company Overview</h1>
                            
                            <h2>Company Information</h2>
                            <p><strong>Company:</strong> [Company Name]</p>
                            <p><strong>Website:</strong> [Company Website]</p>
                            
                            <h2>Position Information</h2>
                            <p><strong>Position:</strong> {sampleData.jobTitle}</p>
                            
                            <h2>Job Satisfaction</h2>
                            <p>No specific job satisfaction data available from provided sources.</p>
                            
                            <h2>Career Growth Opportunities</h2>
                            <p>No specific career growth information available from provided sources.</p>
                            
                            <h2>Compensation and Benefits</h2>
                            <p>No specific compensation and benefits information available from provided sources.</p>
                            
                            <h2>Pros and Cons</h2>
                            <p>No employee review data available from provided sources.</p>
                          </div>
                          <div className="company-ai-disclaimer">
                            <small>⚠️ This information is gathered from anonymous sources and we do not guarantee its correctness. Please verify important details independently.</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'people' && (
                    <div className="people-content">
                      <div className="people-header">
                        <h4>People at [Company Name]</h4>
                        <p className="people-subtitle">Connect with professionals who hold similar roles at [Company Name].</p>
                      </div>
                      <div className="people-grid">
                        <div className="people-card">
                          <div className="people-card-header">
                            <div className="people-avatar">
                              <div className="people-avatar-placeholder">JD</div>
                            </div>
                            <div className="people-info">
                              <h5 className="people-name">John D.</h5>
                              <p className="people-title">Senior Software Engineer</p>
                              <p className="people-company">[Company Name]</p>
                              <p className="people-location">📍 San Francisco, CA, USA</p>
                            </div>
                          </div>
                          <div className="people-card-footer">
                            <div className="people-social-links">
                              <div className="people-social-link" title="LinkedIn Profile">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path>
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="people-card">
                          <div className="people-card-header">
                            <div className="people-avatar">
                              <div className="people-avatar-placeholder">SM</div>
                            </div>
                            <div className="people-info">
                              <h5 className="people-name">Sarah M.</h5>
                              <p className="people-title">Engineering Manager</p>
                              <p className="people-company">[Company Name]</p>
                              <p className="people-location">📍 New York, NY, USA</p>
                            </div>
                          </div>
                          <div className="people-card-footer">
                            <div className="people-social-links">
                              <div className="people-social-link" title="LinkedIn Profile">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path>
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="people-card">
                          <div className="people-card-header">
                            <div className="people-avatar">
                              <div className="people-avatar-placeholder">RK</div>
                            </div>
                            <div className="people-info">
                              <h5 className="people-name">Robert K.</h5>
                              <p className="people-title">Platform Engineer</p>
                              <p className="people-company">[Company Name]</p>
                              <p className="people-location">📍 Seattle, WA, USA</p>
                            </div>
                          </div>
                          <div className="people-card-footer">
                            <div className="people-social-links">
                              <div className="people-social-link" title="LinkedIn Profile">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path>
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="people-card">
                          <div className="people-card-header">
                            <div className="people-avatar">
                              <div className="people-avatar-placeholder">AL</div>
                            </div>
                            <div className="people-info">
                              <h5 className="people-name">Alex L.</h5>
                              <p className="people-title">Senior Developer</p>
                              <p className="people-company">[Company Name]</p>
                              <p className="people-location">📍 Austin, TX, USA</p>
                            </div>
                          </div>
                          <div className="people-card-footer">
                            <div className="people-social-links">
                              <div className="people-social-link" title="LinkedIn Profile">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path>
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'questions' && (
                    <div className="questions-content">
                      <div className="questions-header">
                        <h4>Interview Question Bank</h4>
                        <p className="questions-subtitle">Practice with interview questions tailored to this position. Click "Show Suggestion" to reveal insights and suggested answers.</p>
                      </div>
                      <div className="question-type-tabs">
                        <button className="question-type-tab active">
                          Behavioral
                          <span className="tab-count">(10)</span>
                        </button>
                        <button className="question-type-tab">
                          Technical
                          <span className="tab-count">(49)</span>
                        </button>
                        <button className="question-type-tab">
                          Coding
                          <span className="tab-count">(49)</span>
                        </button>
                      </div>
                      <div className="flash-cards-container">
                        <div className="questions-section">
                          <div className="flash-card">
                            <div className="flash-card-header">
                              <div className="flash-card-question">
                                <h5>Tell me about a time when you had to collaborate with multiple teams to deliver a complex feature.</h5>
                              </div>
                            </div>
                            <div className="coding-question-actions">
                              <button className="btn btn-outline-primary btn-sm show-solution-btn">Show Suggestion</button>
                              <button className="btn btn-sm mark-done-btn btn-outline-secondary" title="Mark as done">Mark As Done</button>
                            </div>
                          </div>
                          <div className="flash-card">
                            <div className="flash-card-header">
                              <div className="flash-card-question">
                                <h5>Describe a situation where you had to optimize the performance of an application.</h5>
                              </div>
                            </div>
                            <div className="coding-question-actions">
                              <button className="btn btn-outline-primary btn-sm show-solution-btn">Show Suggestion</button>
                              <button className="btn btn-sm mark-done-btn btn-outline-secondary" title="Mark as done">Mark As Done</button>
                            </div>
                          </div>
                          <div className="flash-card">
                            <div className="flash-card-header">
                              <div className="flash-card-question">
                                <h5>How do you handle conflicting feedback from different stakeholders on a project?</h5>
                              </div>
                            </div>
                            <div className="coding-question-actions">
                              <button className="btn btn-outline-primary btn-sm show-solution-btn">Show Suggestion</button>
                              <button className="btn btn-sm mark-done-btn btn-outline-secondary" title="Mark as done">Mark As Done</button>
                            </div>
                          </div>
                          <div className="flash-card">
                            <div className="flash-card-header">
                              <div className="flash-card-question">
                                <h5>Tell me about a time you made a mistake in a project and how you handled it.</h5>
                              </div>
                            </div>
                            <div className="coding-question-actions">
                              <button className="btn btn-outline-primary btn-sm show-solution-btn">Show Suggestion</button>
                              <button className="btn btn-sm mark-done-btn btn-outline-secondary" title="Mark as done">Mark As Done</button>
                            </div>
                          </div>
                          <div className="flash-card">
                            <div className="flash-card-header">
                              <div className="flash-card-question">
                                <h5>Describe a project where you had to learn a new technology or framework quickly.</h5>
                              </div>
                            </div>
                            <div className="coding-question-actions">
                              <button className="btn btn-outline-primary btn-sm show-solution-btn">Show Suggestion</button>
                              <button className="btn btn-sm mark-done-btn btn-outline-secondary" title="Mark as done">Mark As Done</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default InterviewPreview

